import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
  timeout: 30000,
});

// --- Auth helpers ---
// "Remember me" → localStorage (persists across browser restarts).
// Off → sessionStorage (cleared when the browser/tab is closed).
const SESSION_KEY = "postcue.session";

export function saveSession(user, { remember = true } = {}) {
  const value = JSON.stringify(user);
  if (remember) {
    localStorage.setItem(SESSION_KEY, value);
    sessionStorage.removeItem(SESSION_KEY);
  } else {
    sessionStorage.setItem(SESSION_KEY, value);
    localStorage.removeItem(SESSION_KEY);
  }
}
export function getSession() {
  try {
    const raw =
      localStorage.getItem(SESSION_KEY) || sessionStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}
export function clearSession() {
  localStorage.removeItem(SESSION_KEY);
  sessionStorage.removeItem(SESSION_KEY);
}

// --- Endpoints ---
export async function login(email, password) {
  const { data } = await api.post("/login", { email, password });
  return data;
}

export async function checkAccount(email) {
  const { data } = await api.post("/auth/check-account", { email });
  return data;
}

export async function applyUpgradeCode(userRecordId, code) {
  const { data } = await api.post("/auth/apply-upgrade-code", {
    user_record_id: userRecordId,
    code,
  });
  return data;
}

export async function fetchHomeVideos(dealer) {
  const { data } = await api.get("/postcue/home-videos", {
    params: dealer ? { dealer } : {},
  });
  return data;
}

export async function fetchHomeVideoKeys(callerUserRecordId) {
  const { data } = await api.get("/postcue/home-video-keys", {
    params: { caller_user_record_id: callerUserRecordId },
  });
  return data;
}

export async function addHomeVideo(callerUserRecordId, key, url, title) {
  const { data } = await api.post("/postcue/home-video", {
    caller_user_record_id: callerUserRecordId,
    key,
    url,
    title,
  });
  return data;
}

export async function removeHomeVideo(callerUserRecordId, key) {
  const { data } = await api.delete("/postcue/home-video", {
    params: { caller_user_record_id: callerUserRecordId, key },
  });
  return data;
}

export async function fetchHomePhotos(dealer) {
  const { data } = await api.get("/postcue/home-photos", {
    params: dealer ? { dealer } : {},
  });
  return data;
}

export async function fetchGallerySliderKeys(callerUserRecordId) {
  const { data } = await api.get("/postcue/gallery-slider-keys", {
    params: { caller_user_record_id: callerUserRecordId },
  });
  return data;
}

export async function fetchGalleryMasterItems(callerUserRecordId) {
  const { data } = await api.get("/postcue/gallery-master-items", {
    params: { caller_user_record_id: callerUserRecordId },
  });
  return data;
}

export async function toggleGallerySlider(callerUserRecordId, url, title, on, rowId) {
  const { data } = await api.post("/postcue/gallery-slider", {
    caller_user_record_id: callerUserRecordId,
    url,
    title,
    on,
    row_id: rowId || undefined,
  });
  return data;
}

export async function fetchSmsQuota(userRecordId) {
  const { data } = await api.get("/postcue/sms-quota", {
    params: { user_record_id: userRecordId },
  });
  return data;
}

export async function sendSms({ userRecordId, message, linkUrl, imageUrl, recipients }) {
  const { data } = await api.post("/postcue/send-sms", {
    user_record_id: userRecordId,
    message,
    link_url: linkUrl,
    image_url: imageUrl,
    recipients,
  });
  return data;
}

export async function submitSmsOptin({ phone, consent, firstName }) {
  const { data } = await api.post("/postcue/sms-optin", {
    phone,
    consent,
    first_name: firstName,
    source: "web_optin_form",
  });
  return data;
}

export async function sendOtp(phone) {
  const { data } = await api.post("/auth/send-otp", { phone });
  return data;
}

export async function verifyOtp(phone, code) {
  const { data } = await api.post("/auth/verify-otp", { phone, code });
  return data;
}

export async function selectAccount(selectToken, userRecordId) {
  const { data } = await api.post("/auth/select-account", {
    select_token: selectToken,
    user_record_id: userRecordId,
  });
  return data;
}

export async function lookupCoupon(code) {
  const { data } = await api.get(`/auth/lookup-coupon/${encodeURIComponent(code)}`);
  return data;
}

export async function signup(payload) {
  const { data } = await api.post("/auth/signup", payload);
  return data;
}

export async function requestPasswordReset(identifier, method = "Email") {
  // Backend accepts either {email,...} or {phone,...}. Pick the right key.
  const isPhone = /[\d+]/.test(identifier) && !identifier.includes("@");
  const body = isPhone
    ? { phone: identifier, method: method || "SMS" }
    : { email: identifier, method: method || "Email" };
  const { data } = await api.post("/auth/reset-password/request", body);
  return data;
}

export async function verifyPasswordReset(identifier, code, new_password) {
  const isPhone = /[\d+]/.test(identifier) && !identifier.includes("@");
  const body = isPhone
    ? { phone: identifier, code, new_password }
    : { email: identifier, code, new_password };
  const { data } = await api.post("/auth/reset-password/verify", body);
  return data;
}

export async function getSmtp(userRecordId) {
  const { data } = await api.get(`/postcue/smtp/${userRecordId}`);
  return data;
}

export async function saveSmtp(payload) {
  const { data } = await api.post(`/postcue/smtp`, payload);
  return data;
}

// Multiple SMTP "send-from" accounts (admin only)
export async function listSmtpSenders(userRecordId) {
  const { data } = await api.get(`/postcue/smtp-senders`, {
    params: { user_record_id: userRecordId },
  });
  return data?.senders || [];
}

export async function addSmtpSender(payload) {
  const { data } = await api.post(`/postcue/smtp-senders`, payload);
  return data;
}

export async function deleteSmtpSender(userRecordId, senderId) {
  const { data } = await api.post(`/postcue/smtp-senders/delete`, {
    user_record_id: userRecordId,
    sender_id: senderId,
  });
  return data;
}

export async function msEmailStatus(userRecordId) {  const { data } = await api.get(`/auth/microsoft/status?user_record_id=${encodeURIComponent(userRecordId)}`);
  return data;
}
export async function msEmailLoginUrl(userRecordId, mode) {
  const redirectUri = `${API_BASE}/auth/microsoft/callback`;
  const extra = mode ? `&mode=${encodeURIComponent(mode)}` : "";
  const { data } = await api.get(
    `/auth/microsoft/login?user_record_id=${encodeURIComponent(userRecordId)}&redirect_uri=${encodeURIComponent(redirectUri)}${extra}`
  );
  return data.url;
}

// Extra OAuth Outlook "send-from" mailboxes (admin only)
export async function listMsSenders(userRecordId) {
  const { data } = await api.get(`/postcue/ms-senders`, {
    params: { user_record_id: userRecordId },
  });
  return data?.senders || [];
}
export async function renameMsSender(userRecordId, accountId, label) {
  const { data } = await api.post(`/postcue/ms-senders/rename`, {
    user_record_id: userRecordId, account_id: accountId, label,
  });
  return data;
}
export async function deleteMsSender(userRecordId, accountId) {
  const { data } = await api.post(`/postcue/ms-senders/delete`, {
    user_record_id: userRecordId, account_id: accountId,
  });
  return data;
}
export async function msEmailDisconnect(userRecordId) {
  const { data } = await api.post(`/auth/microsoft/disconnect`, { user_record_id: userRecordId });
  return data;
}
export async function sendTestEmail(userRecordId, toEmail) {
  const { data } = await api.post(`/postcue/email/test`, { user_record_id: userRecordId, to_email: toEmail });
  return data;
}

export async function getEmailPref(userRecordId) {
  const { data } = await api.get(`/postcue/email/preference/${encodeURIComponent(userRecordId)}`);
  return data;
}
export async function setEmailPref(userRecordId, method) {
  const { data } = await api.post(`/postcue/email/preference`, { user_record_id: userRecordId, method });
  return data;
}

export async function aiQaList(userRecordId) {
  const { data } = await api.get(`/postcue/ai/qa?user_record_id=${encodeURIComponent(userRecordId)}`);
  return data;
}
export async function aiQaCreate(payload) {
  const { data } = await api.post(`/postcue/ai/qa`, payload);
  return data;
}
export async function aiQaUpdate(rowId, payload) {
  const { data } = await api.patch(`/postcue/ai/qa/${rowId}`, payload);
  return data;
}
export async function aiQaDelete(rowId, userRecordId) {
  const { data } = await api.delete(`/postcue/ai/qa/${rowId}?user_record_id=${encodeURIComponent(userRecordId)}`);
  return data;
}

export async function designListTemplates(userRecordId, archived = false) {
  const { data } = await api.get(`/postcue/design/templates?user_record_id=${encodeURIComponent(userRecordId)}${archived ? "&archived=true" : ""}`);
  return data;
}
export async function designGetTemplate(rowId, userRecordId) {
  const { data } = await api.get(`/postcue/design/templates/${rowId}?user_record_id=${encodeURIComponent(userRecordId)}`);
  return data;
}
export async function designUpdateTemplate(rowId, payload) {
  const { data } = await api.patch(`/postcue/design/templates/${rowId}`, payload);
  return data;
}
export async function designCreateTemplate(payload) {
  const { data } = await api.post(`/postcue/design/templates`, payload);
  return data;
}
export async function designAiCompose(payload) {
  const { data } = await api.post(`/postcue/design/ai-compose`, payload, { timeout: 180000 });
  return data;
}
export async function designGetDraft(templateId, userRecordId) {
  const { data } = await api.get(`/postcue/design/draft/${templateId}?user_record_id=${encodeURIComponent(userRecordId)}`);
  return data;
}
export async function designDealerSearch(userRecordId, q = "") {
  const { data } = await api.get(`/postcue/design/dealers?user_record_id=${encodeURIComponent(userRecordId)}&q=${encodeURIComponent(q)}`);
  return data;
}
export async function aiGetOpenAiKey(userRecordId) {
  const { data } = await api.get(`/postcue/ai/openai-key?user_record_id=${encodeURIComponent(userRecordId)}`);
  return data;
}
export async function aiSetOpenAiKey(userRecordId, apiKey) {
  const { data } = await api.post(`/postcue/ai/openai-key`, { user_record_id: userRecordId, api_key: apiKey });
  return data;
}
export async function aiDeleteOpenAiKey(userRecordId) {
  const { data } = await api.post(`/postcue/ai/openai-key/delete`, { user_record_id: userRecordId });
  return data;
}
export async function aiSetDefaultProvider(userRecordId, provider) {
  const { data } = await api.post(`/postcue/ai/default-provider`, { user_record_id: userRecordId, provider });
  return data;
}
export async function designAiScan(userRecordId, urls) {
  const { data } = await api.post(`/postcue/design/ai-scan`, { user_record_id: userRecordId, urls }, { timeout: 120000 });
  return data;
}
export async function aiHelperStateGet(userRecordId, kind) {
  const { data } = await api.get(`/postcue/design/ai-helper-state?user_record_id=${encodeURIComponent(userRecordId)}&kind=${encodeURIComponent(kind)}`);
  return data;
}
export async function aiHelperStateSet(userRecordId, kind, state) {
  const { data } = await api.post(`/postcue/design/ai-helper-state`, { user_record_id: userRecordId, kind, state });
  return data;
}
export function visitorId() {
  try {
    let v = localStorage.getItem("postcue.visitor");
    if (!v) { v = (crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`).replace(/[^a-zA-Z0-9_-]/g, ""); localStorage.setItem("postcue.visitor", v); }
    return v;
  } catch { return ""; }
}
export async function guestSyncCart(dealerSlug, items, extra = {}) {
  const { data } = await api.post(`/postcue/cart/guest-sync`, { visitor_id: visitorId(), dealer_slug: dealerSlug, items, ...extra });
  return data;
}
export async function recordShareClick(dealerSlug, productSlug) {
  const { data } = await api.post(`/postcue/share/click`, { dealer_slug: dealerSlug, product_slug: productSlug, visitor_id: visitorId() });
  return data;
}
export function readShareRef() {
  try {
    const raw = localStorage.getItem("postcue.share_ref");
    if (!raw) return undefined;
    const ref = JSON.parse(raw);
    if (!ref?.dealer_slug || Date.now() - (ref.at || 0) > 30 * 24 * 3600 * 1000) return undefined;
    return { dealer_slug: ref.dealer_slug, product_slug: ref.product_slug || "", visitor_id: visitorId() };
  } catch { return undefined; }
}
export async function galleryImportUrl(userRecordId, url, name) {
  const { data } = await api.post(`/postcue/gallery/import-url`, { user_record_id: userRecordId, url, name }, { timeout: 60000 });
  return data;
}
export async function registerGalleryImage(userRecordId, url, title) {
  const { data } = await api.post(`/postcue/gallery/register`, { user_record_id: userRecordId, url, title });
  return data;
}
export async function getAiPoints(userRecordId) {
  const { data } = await api.get(`/postcue/ai/points?user_record_id=${encodeURIComponent(userRecordId)}`);
  return data;
}
export async function fetchGalleryTable(userRecordId) {
  const { data } = await api.get(`/postcue/gallery-table?user_record_id=${encodeURIComponent(userRecordId)}`);
  return data;
}
export async function designPreviewContext(userRecordId) {
  const { data } = await api.get(`/postcue/design/preview-context?user_record_id=${encodeURIComponent(userRecordId)}`);
  return data;
}
export async function emailPreviewContext(userRecordId, contactRecordId, orderRecordId) {
  const { data } = await api.get(`/postcue/email/preview-context?user_record_id=${encodeURIComponent(userRecordId)}&contact_record_id=${encodeURIComponent(contactRecordId || "")}&order_record_id=${encodeURIComponent(orderRecordId || "")}`);
  return data;
}
export async function fetchOrderContacts(userRecordId, statuses, days) {
  const { data } = await api.get(`/postcue/crm/order-contacts?user_record_id=${encodeURIComponent(userRecordId)}&statuses=${encodeURIComponent((statuses || []).join(","))}&days=${encodeURIComponent(days || 90)}`);
  return data;
}
export async function fetchLastOrders(userRecordId, contactIds, statuses = []) {
  const { data } = await api.get(`/postcue/crm/last-orders?user_record_id=${encodeURIComponent(userRecordId)}&contact_ids=${encodeURIComponent((contactIds || []).join(","))}&statuses=${encodeURIComponent((statuses || []).join(","))}`);
  return data;
}
export async function fetchTriggerContacts(userRecordId, kind, days = 0) {
  const { data } = await api.get(`/postcue/design/trigger-contacts?user_record_id=${encodeURIComponent(userRecordId)}&kind=${encodeURIComponent(kind)}&days=${encodeURIComponent(days || 0)}`);
  return data;
}
export async function fetchDesignTriggers(templateId, userRecordId) {
  const { data } = await api.get(`/postcue/design/triggers/${encodeURIComponent(templateId)}?user_record_id=${encodeURIComponent(userRecordId)}`);
  return data;
}
export async function saveDesignTriggers(templateId, userRecordId, body) {
  const { data } = await api.post(`/postcue/design/triggers/${encodeURIComponent(templateId)}`, { user_record_id: userRecordId, ...body });
  return data;
}
export async function fetchFulfilmentAgentMe(userRecordId) {
  const { data } = await api.get(`/postcue/fulfilment-agent/me?user_record_id=${encodeURIComponent(userRecordId)}`);
  return data;
}
export async function getOrderEmailSettings(userRecordId) {
  const { data } = await api.get(`/postcue/order-emails/settings?user_record_id=${encodeURIComponent(userRecordId)}`);
  return data;
}
export async function setOrderEmailSettings(userRecordId, patch) {
  const { data } = await api.post(`/postcue/order-emails/settings`, { user_record_id: userRecordId, ...patch });
  return data;
}
// ---- SMS reply inbox (admin) ----
export async function fetchSmsInbox(userRecordId, refresh = false) {
  const { data } = await api.get(`/postcue/sms-inbox?user_record_id=${encodeURIComponent(userRecordId)}${refresh ? "&refresh=1" : ""}`);
  return data;
}
export async function fetchSmsThread(userRecordId, phone) {
  const { data } = await api.get(`/postcue/sms-inbox/thread?user_record_id=${encodeURIComponent(userRecordId)}&phone=${encodeURIComponent(phone)}`);
  return data;
}
export async function replySmsInbox(userRecordId, phone, body) {
  const { data } = await api.post(`/postcue/sms-inbox/reply`, { user_record_id: userRecordId, phone, body });
  return data;
}
export async function sendOrderEmailNow(userRecordId, orderRecordId, kind, to) {
  const { data } = await api.post(`/postcue/order-emails/send`, { user_record_id: userRecordId, order_record_id: orderRecordId, kind, to: to || "" });
  return data;
}
export async function fetchCart(userRecordId) {
  const { data } = await api.get(`/postcue/cart?user_record_id=${encodeURIComponent(userRecordId)}`);
  return data;
}
export async function syncCart(userRecordId, items, couponCode) {
  const { data } = await api.post(`/postcue/cart/sync`, {
    user_record_id: userRecordId,
    items,
    coupon_code: couponCode || "",
  });
  return data;
}

export async function activateAccount(userRecordId) {
  const { data } = await api.post("/postcue/activation/activate", { user_record_id: userRecordId });
  return data;
}
export async function pingSession(userRecordId) {
  const { data } = await api.post("/auth/ping", { user_record_id: userRecordId });
  return data;
}
export async function splitOrder(orderRecordId, items, callerRecId, note = "", notify = true) {
  const { data } = await api.post(`/postcue/orders/${orderRecordId}/split`, { caller_user_record_id: callerRecId, items, note, notify });
  return data;
}
export async function fetchBackorders(callerRecId) {
  const { data } = await api.get(`/postcue/orders/backorders?caller_user_record_id=${encodeURIComponent(callerRecId)}`);
  return data;
}
export async function releaseBackorder(orderRecordId, callerRecId, push = true) {
  const { data } = await api.post(`/postcue/orders/${orderRecordId}/release-backorder`, { caller_user_record_id: callerRecId, push });
  return data;
}
export async function activationSeen(userRecordId) {
  const { data } = await api.post("/postcue/activation/seen", { user_record_id: userRecordId });
  return data;
}
export async function fetchActivationReport(callerRecId) {
  const { data } = await api.get(`/postcue/activation/report?caller_user_record_id=${encodeURIComponent(callerRecId)}`);
  return data;
}

export async function getProfile(userRecordId) {
  const { data } = await api.get(`/postcue/profile/${userRecordId}`);
  return data;
}

export async function saveProfile(payload) {
  const { data } = await api.post(`/postcue/profile`, payload);
  return data;
}

export async function uploadImage(file, userRecordId, kind = "photo") {
  const form = new FormData();
  form.append("file", file);
  form.append("user_record_id", userRecordId);
  form.append("kind", kind);
  const { data } = await api.post(`/postcue/upload`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  // Convert relative URL to absolute
  const absUrl = data.url?.startsWith("http") ? data.url : `${BACKEND_URL}${data.url}`;
  return { ...data, url: absUrl };
}

export async function listEmailTemplates() {
  const { data } = await api.get(`/postcue/templates`);
  return data;
}

export async function sendEmail(payload) {
  const { data } = await api.post(`/postcue/send-email`, payload);
  return data;
}

export async function getTwilioSettings(userRecordId) {
  const { data } = await api.get(`/postcue/twilio/${userRecordId}`);
  return data;
}

export async function changePassword(userRecordId, newPassword) {
  const { data } = await api.post(`/auth/change-password`, {
    user_record_id: userRecordId,
    new_password: newPassword,
  });
  return data;
}

export async function fetchPublicDealer(slug) {
  const { data } = await api.get(`/postcue/public/${slug}`);
  return data;
}

export async function fetchMyTeam(user) {
  // POST /api/crm/my-team — returns { success, team_members, total, commission_perc, summary }
  const payload = {
    user_record_id: user.user_record_id || user.record_id,
    ambassador_id: user.user_record_id || user.record_id,
    user_id: String(user.user_id || user.id || ""),
  };
  const { data } = await api.post("/crm/my-team", payload);
  return data;
}

export async function fetchDealerCoupons(userRecordId) {
  const { data } = await api.get(`/crm/dealer-coupons/${userRecordId}`);
  return data;
}

// --- Postcue (Airtable-backed) endpoints ---
export async function fetchCoupons(userId) {
  const { data } = await api.get(`/postcue/coupons/${userId}`);
  return data;
}

export async function applyCartCoupon(callerUserRecordId, targetUserRecordId, couponCode) {
  const { data } = await api.post(`/postcue/admin/cart/apply-coupon`, {
    caller_user_record_id: callerUserRecordId,
    target_user_record_id: targetUserRecordId,
    coupon_code: couponCode,
  });
  return data;
}

export async function awardPoints(callerUserRecordId, targetUserRecordId, amount) {
  const { data } = await api.post(`/postcue/admin/award-points`, {
    caller_user_record_id: callerUserRecordId,
    user_record_id: targetUserRecordId,
    amount,
  });
  return data;
}

export async function awardAiPoints(callerUserRecordId, targetUserRecordId, amount) {
  const { data } = await api.post(`/postcue/admin/award-ai-points`, {
    caller_user_record_id: callerUserRecordId,
    user_record_id: targetUserRecordId,
    amount,
  });
  return data;
}

export async function deleteCoupon(couponRecordId, userRecordId) {
  const { data } = await api.delete(`/postcue/coupons/${couponRecordId}`, { params: { user_record_id: userRecordId } });
  return data;
}

export async function fetchVideoNudgePreview(callerUserRecordId) {
  const { data } = await api.get(`/postcue/admin/videos/nudge`, { params: { caller_user_record_id: callerUserRecordId } });
  return data;
}

export async function sendVideoNudge(callerUserRecordId, dealerIds) {
  const { data } = await api.post(`/postcue/admin/videos/nudge`, { caller_user_record_id: callerUserRecordId, dealer_ids: dealerIds || [] });
  return data;
}

export async function fetchContactActivity(contactRecordId, callerUserRecordId) {
  const { data } = await api.get(`/postcue/contact/${encodeURIComponent(contactRecordId)}/activity`, { params: { caller_user_record_id: callerUserRecordId } });
  return data;
}

export async function fetchDashboardVideos(userRecordId) {
  const { data } = await api.get(`/postcue/dashboard/${encodeURIComponent(userRecordId)}/videos`);
  return data;
}

export async function createCoupon(payload) {
  const { data } = await api.post(`/postcue/coupons`, payload);
  return data;
}

export async function uploadFile(file, userRecordId, kind = "photo", name = "") {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("user_record_id", userRecordId);
  fd.append("kind", kind);
  if (name) fd.append("name", name);
  const { data } = await api.post("/postcue/upload", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

export async function fetchGallery(userRecordId, kind) {
  const params = new URLSearchParams();
  if (kind) params.set("kind", kind);
  const qs = params.toString();
  const { data } = await api.get(
    `/postcue/gallery/${encodeURIComponent(userRecordId)}${qs ? `?${qs}` : ""}`
  );
  return data;
}

export async function deleteUpload(userRecordId, key) {
  const { data } = await api.delete(
    `/postcue/upload?user_record_id=${encodeURIComponent(userRecordId)}&key=${encodeURIComponent(key)}`
  );
  return data;
}

// --- Contacts editing -----------------------------------------------------

export async function fetchContact(contactRowId, callerUserRecordId) {
  const { data } = await api.get(
    `/postcue/contacts/${encodeURIComponent(contactRowId)}?caller_user_record_id=${encodeURIComponent(callerUserRecordId)}`
  );
  return data;
}

export async function fetchAssignableRoles(callerUserRecordId) {
  const { data } = await api.get(`/postcue/contact-roles/assignable`, { params: { caller_user_record_id: callerUserRecordId } });
  return data;
}

export async function updateContact(contactRowId, callerUserRecordId, fields) {
  const { data } = await api.patch(
    `/postcue/contacts/${encodeURIComponent(contactRowId)}`,
    { caller_user_record_id: callerUserRecordId, ...fields }
  );
  return data;
}

// --- Shipping quote --------------------------------------------------------

export async function fetchShippingQuote({
  sellerId = 1,
  userRole = "customer",
  totalWeight = 0,
} = {}) {
  const params = new URLSearchParams({
    seller_id: String(sellerId || 1),
    user_role: userRole || "customer",
    total_weight: String(totalWeight || 0),
  });
  const { data } = await api.get(`/postcue/shipping/quote?${params.toString()}`);
  return data;
}


// --- Posts library + per-dealer Home tabs --------------------------------

export async function fetchPostsLibrary({ homeOnly = false, userRecordId = "", includeHidden = false } = {}) {
  if (userRecordId) {
    const { data } = await api.get(`/postcue/posts?user_record_id=${encodeURIComponent(userRecordId)}${homeOnly ? "&home_only=true" : ""}${includeHidden ? "&include_hidden=true" : ""}`);
    return data;
  }
  const qs = homeOnly ? "?home_only=true" : "";
  const { data } = await api.get(`/postcue/posts${qs}`);
  return data; // { items: [{slug, title, video_url, html, home, ...}], count }
}

export async function fetchDealerHomePosts(userRecordId) {
  const { data } = await api.get(
    `/postcue/users/${encodeURIComponent(userRecordId)}/home-posts`
  );
  return data; // { saved_slugs, using_defaults, items }
}

export async function toggleDealerHomePost(userRecordId, slug) {
  const { data } = await api.post(
    `/postcue/users/${encodeURIComponent(userRecordId)}/home-posts/toggle`,
    { slug, caller_user_record_id: userRecordId }
  );
  return data; // { added, saved_slugs }
}

export async function saveDealerHomePosts(userRecordId, slugs) {
  const { data } = await api.put(
    `/postcue/users/${encodeURIComponent(userRecordId)}/home-posts`,
    { slugs, caller_user_record_id: userRecordId }
  );
  return data;
}


export async function sendContact({ userRecordId, name, email, message, subject }) {
  const { data } = await api.post(`/postcue/contact`, {
    user_record_id: userRecordId,
    name,
    email,
    message,
    subject,
  });
  return data;
}

export async function fetchMyOrders(userId, limit = 50) {
  const { data } = await api.get(
    `/postcue/my-orders/${encodeURIComponent(userId)}?limit=${limit}`
  );
  return data;
}

export async function fetchTeamOrders(userId) {
  const { data } = await api.get(`/postcue/team-orders/${userId}`);
  return data;
}

export async function fetchTeamCommissions(userRecordId, { fromDate, toDate } = {}) {
  const params = new URLSearchParams();
  if (fromDate) params.set("from_date", fromDate);
  if (toDate) params.set("to_date", toDate);
  const qs = params.toString();
  const { data } = await api.get(
    `/postcue/team-commissions/${encodeURIComponent(userRecordId)}${qs ? `?${qs}` : ""}`
  );
  return data;
}

// ---- Contacts CRUD ----
export async function addContact({
  userRecordId,
  firstName,
  lastName,
  email,
  sms,
  landline,
}) {
  const { data } = await api.post(`/postcue/contacts/add`, {
    user_record_id: userRecordId,
    first_name: firstName,
    last_name: lastName,
    email,
    sms,
    landline,
  });
  return data;
}

export async function previewImportContacts(csvText) {
  const { data } = await api.post(`/postcue/contacts/import/preview`, {
    csv_text: csvText,
  });
  return data;
}

export async function importContacts({ userRecordId, csvText, mapping }) {
  const { data } = await api.post(
    `/postcue/contacts/import`,
    { user_record_id: userRecordId, csv_text: csvText, mapping },
    { timeout: 120000 }
  );
  return data;
}

export async function deleteContacts({ callerUserRecordId, memberIds }) {
  const { data } = await api.post(`/postcue/contacts/delete`, {
    caller_user_record_id: callerUserRecordId,
    member_ids: memberIds,
  });
  return data;
}

export async function checkUsernameAvailable(value, excludeRecId) {
  const params = new URLSearchParams({ value });
  if (excludeRecId) params.set("exclude", excludeRecId);
  const { data } = await api.get(`/postcue/username/check?${params.toString()}`);
  return data;
}

// ---- Admin CRM Directory ----
export function isAdminUser(user) {
  if (!user) return false;
  const email = (user.email_id || user.email || "")
    .toString()
    .toLowerCase()
    .trim();
  // Hard-coded admin emails.
  if (
    email === "factory@orevital.com" ||
    email === "mindfulminerals@outlook.com"
  )
    return true;
  // FD / legacy admins by numeric User_id (FD = 1).
  const uid = Number(user.user_id || user.User_id || user.id || 0);
  if (uid === 1) return true;
  const role = (user.role_name || user.role || "").toString().toLowerCase().trim();
  const ADMIN_ROLES = new Set([
    "admin",
    "administrator",
    "owner",
    "superadmin",
    "super_admin",
    "staff",
    "factory",
    "factory_direct",
    "factorydirect",
    "factory direct",
    "fd",
  ]);
  if (ADMIN_ROLES.has(role)) return true;
  const tid = Number(user.user_type_id || 0);
  return tid === 1 || tid === 2;
}

// True when the Airtable `approved` flag on the Users row is set (approved=1).
export function isApprovedUser(user) {
  if (!user) return false;
  const a = user.approved ?? user.is_approved;
  if (a === 1 || a === true) return true;
  const s = String(a || "").trim().toLowerCase();
  return s === "1" || s === "yes" || s === "true";
}

// Access to CRM (Contacts), Coupons and Events requires an approved account
// (approved=1 in the Airtable Users table). Admins always have access.
export function canAccessCrm(user) {
  return isAdminUser(user) || isApprovedUser(user);
}


export async function fetchAdminUsers({
  callerUserRecordId,
  q = "",
  role = "all",
  group = "all",
  neverOrdered = false,
  lastOrderFrom = "",
  lastOrderTo = "",
  loginWithin = "",
  notOrderedSince = "",
  orderStatus = "",
  approved = "all",
  flags = "",
  emailType = "",
  emailOpened = "",
  sort = "recent_orders",
  limit = 50,
  offset = 0,
}) {
  const params = new URLSearchParams({
    caller_user_record_id: callerUserRecordId,
    sort,
    limit: String(limit),
    offset: String(offset),
  });
  if (q) params.set("q", q);
  if (role && role !== "all") params.set("role", role);
  if (group && group !== "all") params.set("group", group);
  if (neverOrdered) params.set("never_ordered", "1");
  if (lastOrderFrom) params.set("last_order_from", lastOrderFrom);
  if (lastOrderTo) params.set("last_order_to", lastOrderTo);
  if (loginWithin) params.set("login_within", loginWithin);
  if (notOrderedSince) params.set("not_ordered_since", notOrderedSince);
  if (orderStatus) params.set("order_status", orderStatus);
  if (approved && approved !== "all") params.set("approved", approved);
  if (flags) params.set("flags", flags);
  if (emailType) {
    params.set("email_type", emailType);
    params.set("email_opened", emailOpened || "Y");
  }
  const { data } = await api.get(`/postcue/admin/users?${params.toString()}`);
  return data;
}

export async function fetchCrmGroups(callerUserRecordId) {
  const { data } = await api.get(
    `/postcue/admin/crm-groups?caller_user_record_id=${encodeURIComponent(callerUserRecordId)}`
  );
  return data?.groups || [];
}

export async function fetchAdminEmailTypes(callerUserRecordId) {
  const { data } = await api.get(
    `/postcue/admin/email-types?caller_user_record_id=${encodeURIComponent(callerUserRecordId)}`
  );
  // Return the richer campaign objects when available, else fall back to names.
  if (Array.isArray(data?.campaigns) && data.campaigns.length) return data.campaigns;
  return (data?.email_types || []).map((name) => ({ name }));
}

export async function fetchTagsMenu() {
  const { data } = await api.get(`/postcue/tags/menu`);
  return data || { menu: [], more: [] };
}

export async function fetchAllTags() {
  const { data } = await api.get(`/postcue/tags/all`);
  return data?.tags || [];
}

export async function fetchTag(slug) {
  const { data } = await api.get(`/postcue/tags/${encodeURIComponent(slug)}`);
  return data?.tag || null;
}

export async function fetchEvents(callerUserRecordId, dealerSlug) {
  const params = [];
  if (callerUserRecordId) params.push(`caller_user_record_id=${encodeURIComponent(callerUserRecordId)}`);
  if (dealerSlug) params.push(`dealer=${encodeURIComponent(dealerSlug)}`);
  const q = params.length ? `?${params.join("&")}` : "";
  const { data } = await api.get(`/postcue/events${q}`);
  return data || { events: [], stats: {}, cities: [] };
}

export async function fetchFeaturedEvents() {
  const { data } = await api.get(`/postcue/events/featured`);
  return data?.events || [];
}

export async function fetchEventDetail(slug) {
  const { data } = await api.get(`/postcue/events/${encodeURIComponent(slug)}`);
  return data?.event || null;
}

export async function reserveEvent(slug, userRecordId) {
  const { data } = await api.post(
    `/postcue/events/${encodeURIComponent(slug)}/reserve`,
    { user_record_id: userRecordId }
  );
  return data;
}

// ── Support desk (two-way live chat) ──────────────────────────────────────
export async function supportUnread(rec) {
  const { data } = await api.get(`/postcue/support/unread?caller_user_record_id=${encodeURIComponent(rec)}`);
  return data?.count || 0;
}
export async function supportInbox(rec) {
  const { data } = await api.get(`/postcue/support/inbox?caller_user_record_id=${encodeURIComponent(rec)}`);
  return data?.conversations || [];
}
export async function supportConversation(ticketId, rec) {
  const { data } = await api.get(`/postcue/support/conversation/${ticketId}?caller_user_record_id=${encodeURIComponent(rec)}`);
  return data?.messages || [];
}
export async function supportReply(ticketRecordId, message, rec) {
  const { data } = await api.post(`/postcue/support/reply`, {
    ticket_record_id: ticketRecordId, message, caller_user_record_id: rec,
  });
  return data;
}
export async function chatThread(ticketId) {
  const { data } = await api.get(`/postcue/chat/thread/${ticketId}`);
  return data?.messages || [];
}
export async function chatVcard(dealerSlug) {
  const { data } = await api.get(`/postcue/chat/vcard${dealerSlug ? `?dealer=${encodeURIComponent(dealerSlug)}` : ""}`);
  return data;
}

export async function deleteOrder(orderRecordId, userRecordId) {
  const { data } = await api.delete(`/postcue/orders/${orderRecordId}?user_record_id=${encodeURIComponent(userRecordId)}`);
  return data;
}

// ── My Services (dealer spa portal) ───────────────────────────────────────
export async function myservicesOverview(rec) {
  const { data } = await api.get(`/postcue/myservices/overview?caller_user_record_id=${encodeURIComponent(rec)}`);
  return data;
}
export async function addService(payload) {
  const { data } = await api.post(`/postcue/myservices/service`, payload);
  return data;
}
export async function toggleService(id, active, rec) {
  const { data } = await api.post(`/postcue/myservices/service/${id}/toggle`, { active, caller_user_record_id: rec });
  return data;
}
export async function deleteService(id, rec) {
  const { data } = await api.delete(`/postcue/myservices/service/${id}?caller_user_record_id=${encodeURIComponent(rec)}`);
  return data;
}
export async function shareService(id, shared, rec) {
  const { data } = await api.post(`/postcue/myservices/service/${id}/share`, { shared, caller_user_record_id: rec });
  return data;
}
export async function setServiceImage(id, image_url, rec) {
  const { data } = await api.post(`/postcue/myservices/service/${id}/image`, { image_url, caller_user_record_id: rec });
  return data;
}
export async function myservicesBookings(rec) {
  const { data } = await api.get(`/postcue/myservices/bookings?caller_user_record_id=${encodeURIComponent(rec)}`);
  return data?.bookings || [];
}
export async function getAvailability(rec) {
  const { data } = await api.get(`/postcue/myservices/availability?caller_user_record_id=${encodeURIComponent(rec)}`);
  return data || { blocked_dates: [], location_id: "" };
}
export async function saveAvailability(rec, dates) {
  const { data } = await api.post(`/postcue/myservices/availability`, { caller_user_record_id: rec, dates });
  return data;
}
export async function getDaySlots(rec, date) {
  const { data } = await api.get(`/postcue/myservices/day-slots?caller_user_record_id=${encodeURIComponent(rec)}&date=${date}`);
  return data || { slots: [], location_id: "" };
}
export async function blockTimes(rec, date, times) {
  const { data } = await api.post(`/postcue/myservices/block-time`, { caller_user_record_id: rec, date, times });
  return data;
}
export async function listMyEvents(rec) {
  const { data } = await api.get(`/postcue/myservices/events?caller_user_record_id=${encodeURIComponent(rec)}`);
  return data || { events: [], is_admin: false };
}
export async function saveEvent(payload) {
  const { data } = await api.post(`/postcue/myservices/event`, payload);
  return data;
}
export async function toggleEvent(id, field, value, rec) {
  const { data } = await api.post(`/postcue/myservices/event/${id}/toggle`, { field, value, caller_user_record_id: rec });
  return data;
}
export async function deleteEvent(id, rec) {
  const { data } = await api.delete(`/postcue/myservices/event/${id}?caller_user_record_id=${encodeURIComponent(rec)}`);
  return data;
}
export async function getApprovedLocations(rec) {
  const { data } = await api.get(`/postcue/myservices/approved-locations?caller_user_record_id=${encodeURIComponent(rec)}`);
  return data?.users || [];
}
export async function bookService(payload) {
  const { data } = await api.post(`/postcue/services/book`, payload);
  return data;
}
export async function serviceSlots(serviceId, locationId, date) {
  const params = new URLSearchParams({ service_id: serviceId, location_id: locationId || "", date });
  const { data } = await api.get(`/postcue/services/slots?${params.toString()}`);
  return data || { slots: [], duration: 30 };
}



export async function fetchAdminRoles(callerUserRecordId) {
  const { data } = await api.get(
    `/postcue/admin/roles?caller_user_record_id=${encodeURIComponent(callerUserRecordId)}`
  );
  return data;
}

export async function adminUpdateUser(payload) {
  const { data } = await api.post(`/postcue/admin/user`, payload);
  return data;
}

export async function adminSetPassword(callerUserRecordId, userRecordId, newPassword) {
  const { data } = await api.post(`/postcue/admin/set-password`, {
    caller_user_record_id: callerUserRecordId,
    user_record_id: userRecordId,
    new_password: newPassword,
  });
  return data;
}

export async function fetchPendingCarts() {
  const { data } = await api.get(`/postcue/admin/pending-carts`);
  return data;
}

export async function deletePendingCarts(caller, userRecordIds) {
  const { data } = await api.post(`/postcue/admin/pending-carts/delete`, { caller_user_record_id: caller, user_record_ids: userRecordIds });
  return data;
}
export async function fetchPostVideos(userRecordId) {
  const { data } = await api.get(`/postcue/posts/videos?user_record_id=${encodeURIComponent(userRecordId)}`);
  return data;
}
export async function createPost(payload) {
  const { data } = await api.post(`/postcue/posts`, payload);
  return data;
}
export async function updatePost(postRecId, payload) {
  const { data } = await api.patch(`/postcue/posts/${postRecId}`, payload);
  return data;
}
export async function deletePost(postRecId, userRecordId) {
  const { data } = await api.delete(`/postcue/posts/${postRecId}?user_record_id=${encodeURIComponent(userRecordId)}`);
  return data;
}
export async function deleteAdminUsers(caller, userRecordIds, force = false) {
  const { data } = await api.post(`/postcue/admin/users/delete`, { caller_user_record_id: caller, user_record_ids: userRecordIds, force });
  return data;
}

export async function fetchOrders({
  userRecordId,
  scope = "mine",
  status = "all",
  fromDate = "",
  toDate = "",
  dealerUserId = "",
  couponCode = "",
  source = "all",
  limit = 100,
}) {
  const params = new URLSearchParams({
    user_record_id: userRecordId,
    scope,
    status,
    source,
    limit: String(limit),
  });
  if (fromDate) params.set("from_date", fromDate);
  if (toDate) params.set("to_date", toDate);
  if (dealerUserId) params.set("dealer_user_id", dealerUserId);
  if (couponCode) params.set("coupon_code", couponCode);
  const { data } = await api.get(`/postcue/orders?${params.toString()}`);
  return data;
}

export async function sendInvoiceEmail({ userRecordId, invoiceRecordId, toEmail = "" }) {
  const { data } = await api.post(`/postcue/invoice/send-email`, {
    user_record_id: userRecordId,
    invoice_record_id: invoiceRecordId,
    to_email: toEmail,
  });
  return data;
}

// Direct link to download the branded invoice PDF (same design as the email).
export function invoicePdfUrl(userRecordId, invoiceRecordId) {
  const p = new URLSearchParams({
    user_record_id: userRecordId,
    invoice_record_id: invoiceRecordId,
  });
  return `${API_BASE}/postcue/invoice/pdf?${p.toString()}`;
}

// Record an email unsubscribe (opt-out). Called by /page/unsubscribe.
export async function recordUnsubscribe(email) {
  const { data } = await api.get(`/postcue/unsubscribe`, { params: { email } });
  return data;
}

export async function createInvoice(payload) {
  const { data } = await api.post(`/postcue/invoice/create`, payload);
  return data;
}

export async function fetchInvoices(userRecordId, { status, paymentMethod, callerUserRecordId } = {}) {
  const { data } = await api.get(`/postcue/invoices`, {
    params: { user_record_id: userRecordId, status: status || undefined, payment_method: paymentMethod || undefined, caller_user_record_id: callerUserRecordId || undefined },
  });
  return data;
}

export async function updateInvoiceStatus({ userRecordId, invoiceRecordId, status, paidAt }) {
  const { data } = await api.post(`/postcue/invoice/update-status`, {
    user_record_id: userRecordId,
    invoice_record_id: invoiceRecordId,
    status,
    paid_at: paidAt || undefined,
  });
  return data;
}

export async function updateInvoice(payload) {
  const { data } = await api.post(`/postcue/invoice/update`, payload);
  return data;
}

export async function deleteInvoices({ userRecordId, invoiceRecordIds }) {
  const { data } = await api.post(`/postcue/invoice/delete`, {
    user_record_id: userRecordId,
    invoice_record_ids: invoiceRecordIds,
  });
  return data;
}


// ─── Admin dashboard summary ──────────────────────────────────────────────
export async function fetchDashboard(userRecordId, { fromDate, toDate } = {}) {
  const params = {};
  if (fromDate) params.from_date = fromDate;
  if (toDate) params.to_date = toDate;
  const { data } = await api.get(
    `/postcue/dashboard/${encodeURIComponent(userRecordId)}`,
    { params }
  );
  return data;
}

export async function fetchSalesReport(userRecordId, { fromDate, toDate, channel, status, basis } = {}) {
  const params = {};
  if (fromDate) params.from_date = fromDate;
  if (toDate) params.to_date = toDate;
  if (channel) params.channel = channel;
  if (status) params.status = status;
  if (basis) params.basis = basis;
  const { data } = await api.get(`/postcue/sales-report/${encodeURIComponent(userRecordId)}`, { params });
  return data;
}

export async function fetchEquity() {
  const { data } = await api.get(`/postcue/equity`);
  return data;
}

// ─── Email campaigns (throttled drip sender) ──────────────────────────────
export async function createCampaign(payload) {
  const { data } = await api.post(`/postcue/campaigns`, payload);
  return data;
}

export async function listCampaigns(userRecordId) {
  const { data } = await api.get(`/postcue/campaigns`, {
    params: { user_record_id: userRecordId },
  });
  return data;
}

export async function getCampaign(campaignId, userRecordId) {
  const { data } = await api.get(`/postcue/campaigns/${campaignId}`, {
    params: { user_record_id: userRecordId },
  });
  return data;
}

export async function campaignAction(campaignId, userRecordId, action) {
  const { data } = await api.post(`/postcue/campaigns/${campaignId}/action`, {
    user_record_id: userRecordId,
    action,
  });
  return data;
}


// ─── Folio (per-dealer inventory) ─────────────────────────────────────────
export async function fetchFolio(userRecordId) {
  const { data } = await api.get(`/postcue/folio`, {
    params: { user_record_id: userRecordId },
  });
  return data;
}

export async function syncFolio(userRecordId, force = false) {
  const { data } = await api.post(`/postcue/folio/sync`, {
    user_record_id: userRecordId,
    force,
  });
  return data;
}

export async function updateFolioItem({ userRecordId, folioRecordId, inventory }) {
  const { data } = await api.post(`/postcue/folio/update`, {
    user_record_id: userRecordId,
    folio_record_id: folioRecordId,
    inventory,
  });
  return data;
}

export async function addFolioItem({ userRecordId, productRecordId, qty = 1 }) {
  const { data } = await api.post(`/postcue/folio/add`, {
    user_record_id: userRecordId,
    product_record_id: productRecordId,
    qty,
  });
  return data;
}

export async function deleteFolioItems({ userRecordId, folioRecordIds }) {
  const { data } = await api.post(`/postcue/folio/delete`, {
    user_record_id: userRecordId,
    folio_record_ids: folioRecordIds,
  });
  return data;
}


export async function updateOrder(orderRecordId, userRecordId, updates) {
  const { data } = await api.patch(
    `/postcue/orders/${orderRecordId}`,
    { user_record_id: userRecordId, ...updates }
  );
  return data;
}

export async function fetchOrderItems(orderRecordId, userRecordId) {
  const { data } = await api.get(
    `/postcue/orders/${orderRecordId}/items?user_record_id=${encodeURIComponent(userRecordId)}`
  );
  return data;
}

export async function fetchCommissionBalance(userRecordId) {
  const { data } = await api.get(
    `/postcue/commission-balance/${encodeURIComponent(userRecordId)}`
  );
  return data;
}

export async function fetchCommissionLedger(userRecordId) {
  const { data } = await api.get(
    `/postcue/commission-ledger/${encodeURIComponent(userRecordId)}`
  );
  return data;
}

export async function publicCheckout({
  dealerSlug,
  customer,
  items,
  shipping_charge,
  tax,
  coupon_code,
  payment_method,
  ship_to,
  discount,
  discount_reason,
  rewards_redeemed,
  commission_redeemed,
  notes,
}) {
  const { data } = await api.post(`/postcue/checkout`, {
    dealer_slug: dealerSlug,
    customer,
    items,
    shipping_charge,
    tax,
    coupon_code,
    payment_method,
    ship_to,
    discount,
    discount_reason,
    rewards_redeemed,
    commission_redeemed,
    notes,
    share_ref: readShareRef(),
    origin_host:
      typeof window !== "undefined" ? window.location.hostname : "",
  });
  try { if (data?.success) localStorage.removeItem("postcue.share_ref"); } catch { /* ignore */ }
  return data;
}

export async function createSquarePaymentLink({
  orderRecordId,
  orderNumber,
  totalCents,
  items,
  buyerEmail,
}) {
  const { data } = await api.post(`/postcue/checkout/square-link`, {
    order_record_id: orderRecordId,
    order_number: orderNumber,
    total_cents: totalCents,
    items,
    buyer_email: buyerEmail,
  });
  return data;
}

export async function createStripeSession({ orderRecordId, originUrl, returnPath }) {
  const { data } = await api.post(`/postcue/checkout/stripe-session`, {
    order_record_id: orderRecordId,
    origin_url: originUrl,
    return_path: returnPath,
  });
  return data;
}

export async function getStripeStatus(sessionId) {
  const { data } = await api.get(
    `/postcue/checkout/stripe-status/${encodeURIComponent(sessionId)}`
  );
  return data;
}

export async function searchProducts(q = "", limit = 50, opts = {}) {
  const params = new URLSearchParams({ limit: String(limit) });
  if (q) params.set("q", q);
  if (opts.featured_only) params.set("featured_only", "true");
  if (opts.tier) params.set("tier", opts.tier);
  if (opts.callerUserRecordId) params.set("caller_user_record_id", opts.callerUserRecordId);
  const { data } = await api.get(`/postcue/products/search?${params.toString()}`);
  return data;
}

export async function fetchProtocols(focus = []) {
  const { data } = await api.get(`/postcue/protocols`, { params: { focus: (focus || []).join(",") || undefined } });
  return data;
}
export async function fetchProtocolsMenu() {
  const { data } = await api.get(`/postcue/protocols/menu`);
  return data;
}
export async function renderFlyer(payload) {
  const { data } = await api.post(`/postcue/flyer/render`, payload);
  return data;
}
export async function fetchProtocolFocus(slug) {
  const { data } = await api.get(`/postcue/protocols/focus/${encodeURIComponent(slug)}`);
  return data;
}
export async function fetchProductProtocols(productRecordId) {
  const { data } = await api.get(`/postcue/protocols/product/${encodeURIComponent(productRecordId)}`);
  return data;
}
export async function fetchProductDetail(slugOrId) {
  const { data } = await api.get(
    `/postcue/product-detail/${encodeURIComponent(slugOrId)}`
  );
  return data;
}

// ─── Admin Catalogue ──────────────────────────────────────────────────────

// ---- Fulfilment inventory (admin) ----
export async function fetchAdminInventory(callerUserRecordId, refresh = false) {
  const { data } = await api.get(`/postcue/admin/inventory?caller_user_record_id=${encodeURIComponent(callerUserRecordId)}${refresh ? "&refresh=1" : ""}`);
  return data;
}
export async function syncAdminInventory(callerUserRecordId) {
  const { data } = await api.post(`/postcue/admin/inventory/sync`, { caller_user_record_id: callerUserRecordId });
  return data;
}
export async function updateAdminInventory(callerUserRecordId, fulfilmentRecordId, patch) {
  const { data } = await api.patch(`/postcue/admin/inventory/${fulfilmentRecordId}`, { caller_user_record_id: callerUserRecordId, ...patch });
  return data;
}
export async function adjustAdminInventory(callerUserRecordId, fulfilmentRecordId, qty, note) {
  const { data } = await api.post(`/postcue/admin/inventory/${fulfilmentRecordId}/adjust`, { caller_user_record_id: callerUserRecordId, qty, note });
  return data;
}
export async function setAdminInventoryStartDate(callerUserRecordId, startDate) {
  const { data } = await api.post(`/postcue/admin/inventory/start-date`, { caller_user_record_id: callerUserRecordId, start_date: startDate });
  return data;
}
// ---- Dealer fulfilment fees (admin) ----
const ffQ = (caller, extra = {}) => { const p = new URLSearchParams({ caller_user_record_id: caller }); Object.entries(extra).forEach(([k, v]) => v && p.set(k, v)); return p.toString(); };
export async function fetchFulfilmentFees(caller) { const { data } = await api.get(`/postcue/admin/fulfilment-fees?${ffQ(caller)}`); return data; }
export async function lookupFulfilmentDealer(caller, uid) { const { data } = await api.get(`/postcue/admin/fulfilment-fees/dealer-lookup?${ffQ(caller, { uid })}`); return data; }
export async function addFulfilmentDealer(caller, uid) { const { data } = await api.post(`/postcue/admin/fulfilment-fees/dealers`, { caller_user_record_id: caller, uid }); return data; }
export async function fetchFulfilmentLedger(caller, uid, filters = {}) { const { data } = await api.get(`/postcue/admin/fulfilment-fees/ledger?${ffQ(caller, { uid, ...filters })}`); return data; }
export async function addFulfilmentOrder(caller, body) { const { data } = await api.post(`/postcue/admin/fulfilment-fees/orders`, { caller_user_record_id: caller, ...body }); return data; }
export async function addFulfilmentPayout(caller, body) { const { data } = await api.post(`/postcue/admin/fulfilment-fees/payout`, { caller_user_record_id: caller, ...body }); return data; }
export async function fetchFulfilmentOrders(caller, uid, fromDate, toDate) { const { data } = await api.get(`/postcue/admin/fulfilment-fees/orders?${ffQ(caller, { uid, from_date: fromDate, to_date: toDate })}`); return data; }
export async function applyFulfilmentOrders(caller, uid, orderRecordIds) { const { data } = await api.post(`/postcue/admin/fulfilment-fees/apply-orders`, { caller_user_record_id: caller, uid, order_record_ids: orderRecordIds }); return data; }
export async function unapplyFulfilmentOrder(caller, uid, orderRecordId) { const { data } = await api.post(`/postcue/admin/fulfilment-fees/unapply-order`, { caller_user_record_id: caller, uid, order_record_id: orderRecordId }); return data; }
// ---- ShipStation sync (admin) ----
export async function fetchShipStationStatus(caller) { const { data } = await api.get(`/postcue/admin/shipstation/status?${ffQ(caller)}`); return data; }
export async function updateShipStationSettings(caller, body) { const { data } = await api.post(`/postcue/admin/shipstation/settings`, { caller_user_record_id: caller, ...body }); return data; }
export async function shipStationSyncNow(caller) { const { data } = await api.post(`/postcue/admin/shipstation/sync-now`, { caller_user_record_id: caller }, { timeout: 180000 }); return data; }
export async function shipStationPushOrder(caller, orderNumber, force = false) { const { data } = await api.post(`/postcue/admin/shipstation/push-order`, { caller_user_record_id: caller, order_number: orderNumber, force }, { timeout: 60000 }); return data; }
export async function fetchInventoryOrders(callerUserRecordId, fromDate, toDate) {
  const p = new URLSearchParams({ caller_user_record_id: callerUserRecordId });
  if (fromDate) p.set("from_date", fromDate);
  if (toDate) p.set("to_date", toDate);
  const { data } = await api.get(`/postcue/admin/inventory/orders?${p.toString()}`);
  return data;
}
export async function applyInventoryOrders(callerUserRecordId, orderRecordIds, undo = false) {
  const { data } = await api.post(`/postcue/admin/inventory/apply-orders`, { caller_user_record_id: callerUserRecordId, order_record_ids: orderRecordIds, undo });
  return data;
}
export async function fetchAdminBundle({ callerUserRecordId, productRecordId }) {
  const { data } = await api.get(`/postcue/admin/product/${productRecordId}/bundle?caller_user_record_id=${encodeURIComponent(callerUserRecordId)}`);
  return data;
}
export async function saveAdminBundle({ callerUserRecordId, productRecordId, productRecordIds, recalcPrice = false, discountPct = 0 }) {
  const { data } = await api.post(`/postcue/admin/product/${productRecordId}/bundle`, {
    caller_user_record_id: callerUserRecordId,
    product_record_ids: productRecordIds,
    recalc_price: recalcPrice,
    discount_pct: discountPct,
  });
  return data;
}
export async function fetchAdminCatalogue({
  callerUserRecordId,
  includeInactive = 1,
  q = "",
} = {}) {
  const params = new URLSearchParams({
    caller_user_record_id: callerUserRecordId || "",
    include_inactive: String(includeInactive ? 1 : 0),
  });
  if (q) params.set("q", q);
  const { data } = await api.get(
    `/postcue/admin/catalogue?${params.toString()}`
  );
  return data;
}

export async function adminCreateProduct({ callerUserRecordId, ...fields }) {
  const { data } = await api.post(`/postcue/admin/product`, {
    caller_user_record_id: callerUserRecordId,
    ...fields,
  });
  return data;
}

export async function fetchAdminProductCategories(callerUserRecordId) {
  const { data } = await api.get(
    `/postcue/admin/product-categories?caller_user_record_id=${encodeURIComponent(callerUserRecordId)}`
  );
  return data;
}

export async function adminUpdateProduct({
  callerUserRecordId,
  productRecordId,
  fields,
}) {
  const { data } = await api.patch(
    `/postcue/admin/product/${encodeURIComponent(productRecordId)}`,
    { caller_user_record_id: callerUserRecordId, fields }
  );
  return data;
}

export async function adminSetProductVisible({
  callerUserRecordId,
  productRecordId,
  visible,
}) {
  const { data } = await api.patch(
    `/postcue/admin/product/${encodeURIComponent(productRecordId)}/visible`,
    {
      caller_user_record_id: callerUserRecordId,
      visible: !!visible,
    }
  );
  return data;
}

export async function adminDuplicateProduct({
  callerUserRecordId,
  productRecordId,
  name,
}) {
  const { data } = await api.post(
    `/postcue/admin/product/${encodeURIComponent(productRecordId)}/duplicate`,
    { caller_user_record_id: callerUserRecordId, name }
  );
  return data;
}

export async function adminCreatePromo({
  callerUserRecordId,
  productRecordId,
  buyQty,
  freeQty,
  startDate,
  endDate,
}) {
  const { data } = await api.post(
    `/postcue/admin/product/${encodeURIComponent(productRecordId)}/promo`,
    {
      caller_user_record_id: callerUserRecordId,
      buy_qty: buyQty,
      free_qty: freeQty,
      start_date: startDate || null,
      end_date: endDate || null,
    }
  );
  return data;
}

export async function adminCreateBundle({
  callerUserRecordId,
  productRecordIds,
  name,
  discountPct,
}) {
  const { data } = await api.post(`/postcue/admin/bundle`, {
    caller_user_record_id: callerUserRecordId,
    product_record_ids: productRecordIds,
    name,
    discount_pct: discountPct || 0,
  });
  return data;
}

export async function adminDeleteProduct({
  callerUserRecordId,
  productRecordId,
}) {
  const { data } = await api.delete(
    `/postcue/admin/product/${encodeURIComponent(productRecordId)}?caller_user_record_id=${encodeURIComponent(callerUserRecordId)}`
  );
  return data;
}

// ─── Shipping Rates ─────────────────────────────────────────────────────────
export async function fetchAdminShipping({ callerUserRecordId }) {
  const { data } = await api.get(
    `/postcue/admin/shipping?caller_user_record_id=${encodeURIComponent(callerUserRecordId)}`
  );
  return data;
}

export async function adminCreateShipping({ callerUserRecordId, fields }) {
  const { data } = await api.post(`/postcue/admin/shipping`, {
    caller_user_record_id: callerUserRecordId,
    ...fields,
  });
  return data;
}

export async function adminUpdateShipping({
  callerUserRecordId,
  recordId,
  fields,
}) {
  const { data } = await api.patch(
    `/postcue/admin/shipping/${encodeURIComponent(recordId)}`,
    { caller_user_record_id: callerUserRecordId, ...fields }
  );
  return data;
}

export async function adminDeleteShipping({ callerUserRecordId, recordId }) {
  const { data } = await api.delete(
    `/postcue/admin/shipping/${encodeURIComponent(recordId)}?caller_user_record_id=${encodeURIComponent(callerUserRecordId)}`
  );
  return data;
}

// ─── SEO / Sitemap ──────────────────────────────────────────────────────────
export async function fetchAdminSeo({
  callerUserRecordId,
  pageType = "",
  onlyIndependent = 0,
}) {
  const params = new URLSearchParams({
    caller_user_record_id: callerUserRecordId,
  });
  if (pageType) params.set("page_type", pageType);
  if (onlyIndependent) params.set("only_independent", "1");
  const { data } = await api.get(`/postcue/admin/seo?${params.toString()}`);
  return data;
}

export async function adminCreateSeo({ callerUserRecordId, fields }) {
  const { data } = await api.post(`/postcue/admin/seo`, {
    caller_user_record_id: callerUserRecordId,
    ...fields,
  });
  return data;
}

export async function adminUpdateSeo({
  callerUserRecordId,
  recordId,
  fields,
}) {
  const { data } = await api.patch(
    `/postcue/admin/seo/${encodeURIComponent(recordId)}`,
    { caller_user_record_id: callerUserRecordId, ...fields }
  );
  return data;
}

export async function adminDeleteSeo({ callerUserRecordId, recordId }) {
  const { data } = await api.delete(
    `/postcue/admin/seo/${encodeURIComponent(recordId)}?caller_user_record_id=${encodeURIComponent(callerUserRecordId)}`
  );
  return data;
}


export async function fetchAdminProductPricing({
  callerUserRecordId,
  productRecordId,
}) {
  const { data } = await api.get(
    `/postcue/admin/product/${encodeURIComponent(productRecordId)}/pricing?caller_user_record_id=${encodeURIComponent(callerUserRecordId)}`
  );
  return data;
}

export async function adminUpdateProductPricing({
  callerUserRecordId,
  productRecordId,
  role,
  pricingRecordId,
  msrp,
  rolePrice,
  salePrice,
  saleStart,
  saleEnd,
  visible,
}) {
  const { data } = await api.patch(
    `/postcue/admin/product/${encodeURIComponent(productRecordId)}/pricing`,
    {
      caller_user_record_id: callerUserRecordId,
      pricing_record_id: pricingRecordId,
      role,
      msrp,
      role_price: rolePrice,
      sale_price: salePrice,
      sale_start: saleStart,
      sale_end: saleEnd,
      visible,
    }
  );
  return data;
}

export async function lookupPricing({ productRecordIds, tier }) {
  const { data } = await api.post(`/postcue/pricing`, {
    product_record_ids: productRecordIds,
    tier: tier || "",
  });
  return data;
}

export async function adminImpersonate({
  callerUserRecordId,
  targetUserRecordId,
}) {
  const { data } = await api.post(`/postcue/admin/impersonate`, {
    caller_user_record_id: callerUserRecordId,
    target_user_record_id: targetUserRecordId,
  });
  return data;
}

export async function adminCreateOrder({
  callerUserRecordId,
  userRecordId,
  items,
  shippingCharge,
  tax,
  discount,
  shippingPhone,
  shippingAddress,
  shippingAddress2,
  shippingCity,
  shippingState,
  shippingZip,
  shippingCountry,
  updateBuyerRecord,
  couponCode,
  status,
}) {
  const { data } = await api.post(`/postcue/admin/create-order`, {
    caller_user_record_id: callerUserRecordId,
    user_record_id: userRecordId,
    items,
    shipping_charge: shippingCharge,
    tax,
    discount,
    shipping_phone: shippingPhone,
    shipping_address: shippingAddress,
    shipping_address2: shippingAddress2,
    shipping_city: shippingCity,
    shipping_state: shippingState,
    shipping_zip: shippingZip,
    shipping_country: shippingCountry,
    update_buyer_record: updateBuyerRecord ? 1 : 0,
    coupon_code: couponCode,
    status,
  });
  return data;
}

export async function addOrderItem({
  userRecordId,
  orderRecordId,
  productRecordId,
  qty = 1,
  price,
}) {
  const { data } = await api.post(`/postcue/order-items`, {
    user_record_id: userRecordId,
    order_record_id: orderRecordId,
    product_record_id: productRecordId,
    qty,
    price,
  });
  return data;
}

export async function updateOrderItem(itemRecordId, userRecordId, updates) {
  const { data } = await api.patch(
    `/postcue/order-items/${itemRecordId}`,
    { user_record_id: userRecordId, ...updates }
  );
  return data;
}

export async function deleteOrderItem(itemRecordId, userRecordId) {
  const { data } = await api.delete(
    `/postcue/order-items/${itemRecordId}?user_record_id=${encodeURIComponent(userRecordId)}`
  );
  return data;
}

export async function fetchOrdersYearCompare({
  userRecordId,
  scope = "mine",
  years = "",
  dealerUserId = "",
}) {
  const params = new URLSearchParams({
    user_record_id: userRecordId,
    scope,
  });
  if (years) params.set("years", years);
  if (dealerUserId) params.set("dealer_user_id", dealerUserId);
  const { data } = await api.get(`/postcue/orders/year-compare?${params.toString()}`);
  return data;
}

export async function fetchOrdersYtd({
  userRecordId,
  scope = "mine",
  dealerUserId = "",
  fromDate = "",
  toDate = "",
  compareYear = "",
}) {
  const params = new URLSearchParams({
    user_record_id: userRecordId,
    scope,
  });
  if (dealerUserId) params.set("dealer_user_id", dealerUserId);
  if (fromDate) params.set("from_date", fromDate);
  if (toDate) params.set("to_date", toDate);
  if (compareYear) params.set("compare_year", String(compareYear));
  const { data } = await api.get(`/postcue/orders/ytd?${params.toString()}`);
  return data;
}

export function couponShareUrl(code) {
  // OreVital store with coupon pre-applied. Change once a real /c/ shortener exists.
  return `https://orevital.com/?coupon=${encodeURIComponent(code)}`;
}


// ─── Product Reviews ────────────────────────────────────────────────────────
export async function fetchProductReviews(productRecordId) {
  const { data } = await api.get(
    `/postcue/product/${encodeURIComponent(productRecordId)}/reviews`
  );
  return data;
}

export async function postProductReview({
  productRecordId,
  callerUserRecordId,
  rating,
  subject,
  content,
  dealerHandle,
}) {
  const m = window.location.pathname.match(/^\/d\/([^/?#]+)/);
  const { data } = await api.post(
    `/postcue/product/${encodeURIComponent(productRecordId)}/reviews`,
    {
      caller_user_record_id: callerUserRecordId,
      rating,
      subject,
      content,
      dealer_handle: dealerHandle || (m ? m[1] : ""),
    },
    { timeout: 120000 }
  );
  return data;
}

export async function adminReplyToReview({
  commentRecordId,
  callerUserRecordId,
  content,
}) {
  const { data } = await api.post(
    `/postcue/admin/review/${encodeURIComponent(commentRecordId)}/reply`,
    { caller_user_record_id: callerUserRecordId, content }
  );
  return data;
}

// --- Site footer + widget pages (Airtable `widgets` table) ---
export async function getFooter() {
  const { data } = await api.get("/widgets/footer");
  return data;
}

export async function getWidgetPage(slug) {
  const { data } = await api.get(`/widgets/page/${encodeURIComponent(slug)}`);
  return data;
}

export async function getCouponTiers(callerUserRecordId) {
  const { data } = await api.get("/coupon-tiers", {
    params: { caller_user_record_id: callerUserRecordId },
  });
  return data;
}

// ---- Meta social (Instagram / Facebook)
export async function socialIgStatus(userRecordId) {
  const { data } = await api.get(`/postcue/social/instagram?user_record_id=${encodeURIComponent(userRecordId)}`);
  return data;
}
export async function socialIgConnect(userRecordId, token) {
  const { data } = await api.post(`/postcue/social/instagram/connect`, { user_record_id: userRecordId, token });
  return data;
}
export async function socialIgSettings(userRecordId, patch) {
  const { data } = await api.post(`/postcue/social/instagram/settings`, { user_record_id: userRecordId, ...patch });
  return data;
}
// ---- Forms engine (Airtable Forms / Form_Submissions)
export async function formByToken(token) {
  const { data } = await api.get(`/forms/by-token/${encodeURIComponent(token)}`);
  return data;
}
export async function formShell(token) {
  const { data } = await api.get(`/forms/by-token/${encodeURIComponent(token)}/shell`);
  return data;
}
export async function publicCoupon(couponRecId) {
  const { data } = await api.get(`/public/coupon/${encodeURIComponent(couponRecId)}`);
  return data;
}
export async function formSubmissionResult(subId, userRecordId) {
  const { data } = await api.get(`/postcue/forms/submissions/${encodeURIComponent(subId)}/result?user_record_id=${encodeURIComponent(userRecordId)}`);
  return data;
}
export async function formEmailResult(subId, payload) {
  const { data } = await api.post(`/postcue/forms/submissions/${encodeURIComponent(subId)}/email-result`, payload, { timeout: 60000 });
  return data;
}
export async function publicDealerQa(slug) {
  const { data } = await api.get(`/public/dealer/${encodeURIComponent(slug)}/qa`);
  return data;
}
export async function formSubmit(token, answers) {
  const { data } = await api.post(`/forms/by-token/${encodeURIComponent(token)}/submit`, { answers }, { timeout: 60000 });
  return data;
}
export async function formsList(userRecordId) {
  const { data } = await api.get(`/postcue/forms?user_record_id=${encodeURIComponent(userRecordId)}`);
  return data;
}
export async function formEmbedPreview(userRecordId, formRef) {
  const { data } = await api.get(`/postcue/forms/embed-preview`, { params: { user_record_id: userRecordId, form: formRef } });
  return data;
}
export async function getSetupState(userRecordId) {
  const { data } = await api.get(`/postcue/setup-state`, { params: { user_record_id: userRecordId } });
  return data;
}
export async function fetchSetupReport(callerUserRecordId) {
  const { data } = await api.get(`/postcue/setup-report`, { params: { caller_user_record_id: callerUserRecordId } });
  return data;
}
export async function saveSetupState(userRecordId, patch) {
  const { data } = await api.post(`/postcue/setup-state`, { user_record_id: userRecordId, ...patch });
  return data;
}
export async function formSubmissions(formRecId, userRecordId) {
  const { data } = await api.get(`/postcue/forms/${encodeURIComponent(formRecId)}/submissions?user_record_id=${encodeURIComponent(userRecordId)}`);
  return data;
}
export async function formsSources(userRecordId) {
  const { data } = await api.get(`/postcue/forms/sources?user_record_id=${encodeURIComponent(userRecordId)}`);
  return data;
}
export async function formCreate(payload) {
  const { data } = await api.post(`/postcue/forms`, payload);
  return data;
}
export async function formUpdate(formRecId, payload) {
  const { data } = await api.patch(`/postcue/forms/${encodeURIComponent(formRecId)}`, payload);
  return data;
}
export async function formSendInvite(formRecId, payload) {
  const { data } = await api.post(`/postcue/forms/${encodeURIComponent(formRecId)}/send-invite`, payload, { timeout: 90000 });
  return data;
}
export async function formCreateSubmission(formRecId, payload) {
  const { data } = await api.post(`/postcue/forms/${encodeURIComponent(formRecId)}/submissions`, payload);
  return data;
}
export async function socialIgLoginConfig(userRecordId, appId, appSecret) {
  const { data } = await api.post(`/postcue/social/instagram-login/config`, { user_record_id: userRecordId, app_id: appId, app_secret: appSecret || "" });
  return data;
}
// ---- Dealer-owned Instagram (Business Login for Instagram)
export function dealerIgConnectUrl(userRecordId, next = "/app/profile") {
  return `${API_BASE}/auth/instagram/start?user_record_id=${encodeURIComponent(userRecordId)}&origin=${encodeURIComponent(window.location.origin)}&next=${encodeURIComponent(next)}`;
}
export async function dealerIgStatus(userRecordId) {
  const { data } = await api.get(`/postcue/dealer/instagram?user_record_id=${encodeURIComponent(userRecordId)}`);
  return data;
}
export async function dealerIgTesterRequest(userRecordId, instagram) {
  const { data } = await api.post(`/postcue/dealer/instagram/tester-request`, { user_record_id: userRecordId, instagram });
  return data;
}
export async function dealerIgDisconnect(userRecordId) {
  const { data } = await api.post(`/postcue/dealer/instagram/disconnect`, { user_record_id: userRecordId });
  return data;
}
export async function dealerIgPreviewCard(userRecordId, commentRecordId, style = "classic", fmt = "square") {
  const { data } = await api.post(`/postcue/dealer/instagram/preview-card`, { user_record_id: userRecordId, comment_record_id: commentRecordId, style, fmt }, { timeout: 60000 });
  return data;
}
export async function dealerIgPostReview(userRecordId, commentRecordId, style = "classic", fmt = "square") {
  const { data } = await api.post(`/postcue/dealer/instagram/post-review`, { user_record_id: userRecordId, comment_record_id: commentRecordId, style, fmt }, { timeout: 120000 });
  return data;
}
export async function dealerLaunchCard(userRecordId, fmt = "square") {
  const { data } = await api.post(`/postcue/dealer/launch-card`, { user_record_id: userRecordId, fmt }, { timeout: 60000 });
  return data;
}
export async function dealerIgPostLaunch(userRecordId, imageUrl, caption, fmt = "square") {
  const { data } = await api.post(`/postcue/dealer/instagram/post-launch`, { user_record_id: userRecordId, image_url: imageUrl, caption, fmt }, { timeout: 120000 });
  return data;
}
export async function dealerIgPosts(userRecordId) {
  const { data } = await api.get(`/postcue/dealer/instagram/posts?user_record_id=${encodeURIComponent(userRecordId)}`);
  return data;
}
export async function socialFbConfig(userRecordId, configId) {
  const { data } = await api.post(`/postcue/social/facebook/config`, { user_record_id: userRecordId, config_id: configId });
  return data;
}
export async function socialIgPreviewCard(userRecordId, commentRecordId, style = "classic", fmt = "square") {
  const { data } = await api.post(`/postcue/social/instagram/preview-card`, { user_record_id: userRecordId, comment_record_id: commentRecordId, style, fmt }, { timeout: 60000 });
  return data;
}
export async function socialIgPostReview(userRecordId, commentRecordId, dealerHandle, style = "classic", fmt = "square") {
  const { data } = await api.post(`/postcue/social/instagram/post-review`, { user_record_id: userRecordId, comment_record_id: commentRecordId, dealer_handle: dealerHandle || "", style, fmt }, { timeout: 120000 });
  return data;
}
export async function socialIgPosts(userRecordId) {
  const { data } = await api.get(`/postcue/social/instagram/posts?user_record_id=${encodeURIComponent(userRecordId)}`);
  return data;
}
export async function facebookStatus() {
  const { data } = await api.get(`/auth/facebook/status`);
  return data;
}
export async function facebookExchange(code) {
  const { data } = await api.post(`/auth/facebook/exchange`, { code });
  return data;
}
export async function reviewShareAssets(commentRecordId) {
  const { data } = await api.get(`/postcue/review-share/${encodeURIComponent(commentRecordId)}`, { timeout: 60000 });
  return data;
}
export async function reviewShareClaim(commentRecordId, callerUserRecordId, network, proofUrl = "") {
  const { data } = await api.post(`/postcue/review-share/${encodeURIComponent(commentRecordId)}/claim`, { caller_user_record_id: callerUserRecordId, network, proof_url: proofUrl });
  return data;
}
export async function reviewShareClaims(userRecordId) {
  const { data } = await api.get(`/postcue/review-share-claims?user_record_id=${encodeURIComponent(userRecordId)}`);
  return data;
}
export async function socialIgComments(userRecordId) {
  const { data } = await api.get(`/postcue/social/instagram/comments?user_record_id=${encodeURIComponent(userRecordId)}`, { timeout: 90000 });
  return data;
}
export async function socialIgImportComment(userRecordId, payload) {
  const { data } = await api.post(`/postcue/social/instagram/import-comment`, { user_record_id: userRecordId, ...payload });
  return data;
}
export async function facebookCompleteEmail(pending, email) {
  const { data } = await api.post(`/auth/facebook/complete-email`, { pending, email });
  return data;
}
export function facebookLoginUrl(next) {
  return `${API_BASE}/auth/facebook/login?next=${encodeURIComponent(next || "/")}&origin=${encodeURIComponent(window.location.origin)}`;
}

// ---- ChatGPT Commerce (ACP) — admin layer; secrets never leave the server
const acpQ = (caller) => ({ params: { caller_user_record_id: caller } });
// ---- Promo videos (Marketing → Media)
// ─── AI Slides studio ───────────────────────────────────────────────────────────────────────
export async function slidesConfig(caller) { const { data } = await api.get(`/slides/config`, { params: { caller_user_record_id: caller } }); return data; }
export async function slidesProjects(caller, all = false) { const { data } = await api.get(`/slides/projects`, { params: { caller_user_record_id: caller, all } }); return data; }
export async function slidesSave(caller, project) { const { data } = await api.post(`/slides/projects`, { caller_user_record_id: caller, ...project }); return data; }
export async function slidesGet(caller, id) { const { data } = await api.get(`/slides/projects/${id}`, { params: { caller_user_record_id: caller } }); return data; }
export async function slidesDelete(caller, id) { const { data } = await api.delete(`/slides/projects/${id}`, { params: { caller_user_record_id: caller } }); return data; }
export async function slidesRender(caller, id) { const { data } = await api.post(`/slides/projects/${id}/render`, { caller_user_record_id: caller }); return data; }
export async function slidesCancel(caller, id) { const { data } = await api.post(`/slides/projects/${id}/cancel`, { caller_user_record_id: caller }); return data; }
export async function slidesDuplicate(caller, id) { const { data } = await api.post(`/slides/projects/${id}/duplicate`, { caller_user_record_id: caller }); return data; }
export async function slidesPushLive(caller, id) { const { data } = await api.post(`/slides/projects/${id}/push-live`, { caller_user_record_id: caller }); return data; }
export async function slidesVoiceClone(caller, { name, consent, files }) {
  const fd = new FormData();
  fd.append("caller_user_record_id", caller); fd.append("name", name || ""); fd.append("consent", consent ? "true" : "false");
  files.forEach((f, i) => fd.append("files", f, f.name || `sample-${i + 1}.webm`));
  const { data } = await api.post(`/slides/voices/clone`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 240000 });
  return data;
}
export async function slidesVoiceDelete(caller, voiceId) { const { data } = await api.delete(`/slides/voices/${voiceId}`, { params: { caller_user_record_id: caller } }); return data; }
export async function slidesAiScript(caller, body) { const { data } = await api.post(`/slides/ai-script`, { caller_user_record_id: caller, ...body }); return data; }
export async function slidesVoicePreview(caller, voiceId, text) { const { data } = await api.post(`/slides/voice-preview`, { caller_user_record_id: caller, voice_id: voiceId, text }, { responseType: "blob" }); return data; }
export async function slidesMusicAi(caller, prompt, seconds) { const { data } = await api.post(`/slides/music/ai`, { caller_user_record_id: caller, prompt, seconds }); return data; }
export async function slidesCatalog(caller, q = "") { const { data } = await api.get(`/slides/catalog`, { params: { caller_user_record_id: caller, q } }); return data; }
export async function slidesMediaClips(caller) { const { data } = await api.get(`/slides/media-clips`, { params: { caller_user_record_id: caller } }); return data; }

export async function promoConfig(caller, dealer) { const { data } = await api.get(`/promo-videos/config`, { params: { caller_user_record_id: caller, dealer: dealer || "" } }); return data; }
export async function promoPreviewHtml(form, sec) { const { data } = await api.post(`/promo-videos/preview`, { form, sec }, { responseType: "text" }); return data; }
export async function promoSaveDraft(caller, dealer, form, templateId) { const { data } = await api.post(`/promo-videos/drafts`, { caller_user_record_id: caller, dealer, form, template_id: templateId }); return data; }
export async function promoSaveProfile(caller, dealer, form) { const { data } = await api.post(`/promo-videos/profile`, { caller_user_record_id: caller, dealer, form }); return data; }
export async function promoGenerate(caller, dealer, templateId, form, notifySms = false) { const { data } = await api.post(`/promo-videos/generate`, { caller_user_record_id: caller, dealer, template_id: templateId, form, notify_sms: notifySms }); return data; }
export async function promoNotifyToggle(caller, id, on) { const { data } = await api.post(`/promo-videos/${id}/notify`, { caller_user_record_id: caller, notify_sms: on }); return data; }
export async function promoPreviewHtmlFor(form, sec, templateId) { const { data } = await api.post(`/promo-videos/preview`, { form, sec, template_id: templateId }, { responseType: "text" }); return data; }
export async function promoApprove(caller, id, form) { const { data } = await api.post(`/promo-videos/${id}/approve`, { caller_user_record_id: caller, form }); return data; }
export async function promoMine(caller, dealer) { const { data } = await api.get(`/promo-videos/mine`, { params: { caller_user_record_id: caller, dealer: dealer || "" } }); return data; }
export async function promoGet(caller, id) { const { data } = await api.get(`/promo-videos/${id}`, acpQ(caller)); return data; }
export async function promoDelete(caller, id) { const { data } = await api.delete(`/promo-videos/${id}`, acpQ(caller)); return data; }
export async function promoShare(caller, id) { const { data } = await api.post(`/promo-videos/${id}/share`, { caller_user_record_id: caller }); return data; }
export async function promoPostReel(caller, id, again = false) { const { data } = await api.post(`/promo-videos/${id}/instagram`, { caller_user_record_id: caller, again }); return data; }
export async function promoAdminVideos(caller) { const { data } = await api.get(`/promo-videos/admin/videos`, acpQ(caller)); return data; }
export async function promoAdminTemplates(caller) { const { data } = await api.get(`/promo-videos/admin/templates`, acpQ(caller)); return data; }
export async function promoAdminTemplatePatch(caller, id, patch) { const { data } = await api.patch(`/promo-videos/admin/templates/${id}`, { caller_user_record_id: caller, ...patch }); return data; }
export async function promoAdminVoices(caller) { const { data } = await api.get(`/promo-videos/admin/voices`, acpQ(caller)); return data; }
export async function promoNarrationPreview(caller, dealer, form, templateId, sec = "intro") { const r = await api.post(`/promo-videos/narration-preview`, { caller_user_record_id: caller, dealer, form, template_id: templateId, sec }, { responseType: "blob" }); return { blob: r.data, text: r.headers["x-narration-text"] || "" }; }
export async function promoAdminCancelStuck(caller, dealer) { const { data } = await api.post(`/promo-videos/admin/videos/cancel-stuck`, { caller_user_record_id: caller, dealer: dealer || "" }); return data; }
export async function promoAdminRerender(caller, id) { const { data } = await api.post(`/promo-videos/admin/videos/${id}/rerender`, { caller_user_record_id: caller }); return data; }
export async function acpOverview(caller) { const { data } = await api.get(`/acp/admin/overview`, acpQ(caller)); return data; }
export async function acpProducts(caller) { const { data } = await api.get(`/acp/admin/products`, acpQ(caller)); return data; }
export async function acpPublish(caller, recId, published) { const { data } = await api.post(`/acp/admin/products/${recId}/publish`, { caller_user_record_id: caller, published }); return data; }
export async function acpPreview(caller, recId) { const { data } = await api.get(`/acp/admin/products/${recId}/preview`, acpQ(caller)); return data; }
export async function acpSync(caller) { const { data } = await api.post(`/acp/admin/sync`, { caller_user_record_id: caller }, { timeout: 120000 }); return data; }
export async function acpSyncLog(caller) { const { data } = await api.get(`/acp/admin/sync-log`, acpQ(caller)); return data; }
export async function acpPromotions(caller) { const { data } = await api.get(`/acp/admin/promotions`, acpQ(caller)); return data; }
export async function acpPromotionToggle(caller, recId, eligible) { const { data } = await api.post(`/acp/admin/promotions/${recId}`, { caller_user_record_id: caller, eligible }); return data; }
export async function acpSettingsGet(caller) { const { data } = await api.get(`/acp/admin/settings`, acpQ(caller)); return data; }
export async function acpSettingsSave(caller, patch) { const { data } = await api.post(`/acp/admin/settings`, { caller_user_record_id: caller, ...patch }); return data; }
export async function acpPush(caller) { const { data } = await api.post(`/acp/admin/push`, { caller_user_record_id: caller }, { timeout: 180000 }); return data; }
export async function acpPushLog(caller) { const { data } = await api.get(`/acp/admin/push-log`, acpQ(caller)); return data; }
export async function acpSftpTest(caller) { const { data } = await api.post(`/acp/admin/sftp-test`, { caller_user_record_id: caller }, { timeout: 60000 }); return data; }
